import type { ModelQuad } from './javaModels';
import { textureBaseColors, woodTones } from './palette';

export type RenderLayer = 'solid' | 'translucent';

/** 0〜1のブロックローカル座標で表した直方体。 */
export interface BlockBox {
  from: readonly [number, number, number];
  to: readonly [number, number, number];
}

export interface BlockAppearance {
  quads?: ModelQuad[];
  modelSource?: 'java' | 'special' | 'bedrock';
  /** +X, -X, +Y, -Y, +Z, -Z の順のテクスチャ名。 */
  faces: readonly [string, string, string, string, string, string];
  boxes: BlockBox[];
  layer: RenderLayer;
  /** 立方体を隙間なく埋め、隣接ブロックの面を隠せるか。 */
  occludes: boolean;
  /** 同じブロックが隣接したとき境界面を省けるか（ガラス・葉など）。 */
  mergesWithSame: boolean;
  /** 光源ブロック。陰影とAOを弱めて明るく見せる。 */
  emissive: boolean;
  /** 草花のようにX字の板2枚で描くか。 */
  cross: boolean;
}

const FULL_CUBE: BlockBox = { from: [0, 0, 0], to: [1, 1, 1] };

const TRANSLUCENT = /(glass|water|ice$|_ice$|slime_block|honey_block|nether_portal|bubble)/;
const EMISSIVE = /(glowstone|sea_lantern|shroomlight|froglight|lantern|torch|magma_block|redstone_lamp|_lamp$|jack_o_lantern|beacon|conduit|end_rod|campfire|fire$|lava|candle|glow_)/;
const CROSS = /(_sapling|^sapling|short_grass|^grass$|tall_grass|fern|_fern|_flower|dandelion|poppy|orchid|allium|bluet|tulip|daisy|cornflower|lily_of_the_valley|wither_rose|_mushroom$|sugar_cane|wheat|carrots|potatoes|beetroots|nether_wart$|sweet_berry|dead_bush|seagrass|kelp|vine$|_roots$|sprouts|torchflower|pitcher_plant|spore_blossom|hanging_roots|glow_lichen|cave_vines)/;

const SUFFIXES = [
  '_slab', '_stairs', '_wall', '_fence_gate', '_fence', '_door', '_trapdoor',
  '_pressure_plate', '_button', '_sign', '_pane', '_bars', '_carpet',
];

const explicitFaces: Record<string, { top?: string; bottom?: string; side?: string; front?: string }> = {
  grass_block: { top: 'grass_block_top', side: 'grass_block_side', bottom: 'dirt' },
  podzol: { top: 'podzol_top', side: 'podzol_side', bottom: 'dirt' },
  mycelium: { top: 'mycelium_top', side: 'mycelium_side', bottom: 'dirt' },
  dirt_path: { top: 'dirt_path_top', side: 'dirt_path_side', bottom: 'dirt' },
  farmland: { top: 'farmland', side: 'dirt', bottom: 'dirt' },
  crafting_table: { top: 'crafting_table_top', side: 'crafting_table_side', bottom: 'oak_planks' },
  furnace: { top: 'furnace_top', side: 'furnace_side', front: 'furnace_front' },
  blast_furnace: { top: 'blast_furnace_top', side: 'blast_furnace_side', front: 'blast_furnace_front' },
  smoker: { top: 'smoker_top', side: 'smoker_side', front: 'smoker_front' },
  bookshelf: { top: 'oak_planks', side: 'bookshelf', bottom: 'oak_planks' },
  barrel: { top: 'barrel_top', side: 'barrel_side', bottom: 'barrel_bottom' },
  tnt: { top: 'tnt_top', side: 'tnt_side', bottom: 'tnt_bottom' },
  sandstone: { top: 'sandstone_top', side: 'sandstone', bottom: 'sandstone_bottom' },
  red_sandstone: { top: 'red_sandstone_top', side: 'red_sandstone', bottom: 'red_sandstone_bottom' },
  pumpkin: { top: 'pumpkin_top', side: 'pumpkin_side', bottom: 'pumpkin_top' },
  carved_pumpkin: { top: 'pumpkin_top', side: 'pumpkin_side', front: 'carved_pumpkin_front' },
  jack_o_lantern: { top: 'pumpkin_top', side: 'pumpkin_side', front: 'jack_o_lantern_front' },
  melon: { top: 'melon_top', side: 'melon_side', bottom: 'melon_top' },
  hay_block: { top: 'hay_block_top', side: 'hay_block_side', bottom: 'hay_block_top' },
  bone_block: { top: 'bone_block_top', side: 'bone_block_side', bottom: 'bone_block_top' },
  quartz_pillar: { top: 'quartz_pillar_top', side: 'quartz_pillar', bottom: 'quartz_pillar_top' },
  purpur_pillar: { top: 'purpur_pillar_top', side: 'purpur_pillar', bottom: 'purpur_pillar_top' },
  chest: { top: 'chest_top', side: 'chest_side', front: 'chest_front' },
  observer: { top: 'observer_top', side: 'observer_side', front: 'observer_front' },
  dispenser: { top: 'furnace_top', side: 'furnace_side', front: 'dispenser_front' },
  command_block: { top: 'command_block', side: 'command_block', front: 'command_block_front' },
  repeating_command_block: { top: 'repeating_command_block', side: 'repeating_command_block', front: 'repeating_command_block_front' },
  chain_command_block: { top: 'chain_command_block', side: 'chain_command_block', front: 'chain_command_block_front' },
  dropper: { top: 'furnace_top', side: 'furnace_side', front: 'dropper_front' },
  // ピストンは「押す面」だけ色が違う。粘着かどうかがここで分かる。
  piston: { top: 'piston_side', side: 'piston_side', bottom: 'piston_bottom', front: 'piston_top' },
  sticky_piston: { top: 'piston_side', side: 'piston_side', bottom: 'piston_bottom', front: 'piston_top_sticky' },
  piston_head: { top: 'piston_side', side: 'piston_side', bottom: 'piston_side', front: 'piston_top' },
  sticky_piston_head: { top: 'piston_side', side: 'piston_side', bottom: 'piston_side', front: 'piston_top_sticky' },
  loom: { top: 'loom_top', side: 'loom_side', front: 'loom_front' },
  lectern: { top: 'lectern_top', side: 'lectern_sides', bottom: 'oak_planks' },
  cake: { top: 'cake_top', side: 'cake_side', bottom: 'cake_bottom' },
  composter: { top: 'composter_top', side: 'composter_side', bottom: 'composter_bottom' },
  beehive: { top: 'beehive_end', side: 'beehive_side', bottom: 'beehive_end' },
  note_block: { top: 'note_block', side: 'note_block', bottom: 'note_block' },
  smooth_stone_slab: { top: 'smooth_stone', side: 'smooth_stone_slab_side', bottom: 'smooth_stone' },
};

/** `oak_stairs` → `oak_planks` のように、派生ブロックの素材テクスチャを推定する。 */
export function materialTexture(base: string): string {
  if (woodTones[base]) return `${base}_planks`;
  if (textureBaseColors[base] !== undefined) return base;
  if (textureBaseColors[`${base}s`] !== undefined) return `${base}s`;
  if (textureBaseColors[`${base}_block`] !== undefined) return `${base}_block`;
  if (base.endsWith('_brick')) return `${base}s`;
  return base;
}

function stripNamespace(name: string): string {
  const separator = name.indexOf(':');
  return (separator >= 0 ? name.slice(separator + 1) : name).toLowerCase();
}

function stripSuffix(name: string): { base: string; suffix: string } {
  const suffix = SUFFIXES.find((candidate) => name.endsWith(candidate));
  return suffix ? { base: name.slice(0, -suffix.length), suffix } : { base: name, suffix: '' };
}

function facesFrom(top: string, side: string, bottom: string, front?: string, facing?: string): BlockAppearance['faces'] {
  const faces: [string, string, string, string, string, string] = [side, side, top, bottom, side, side];
  if (front && facing) {
    // ピストンやディスペンサーは上下も向くので、6方向すべてを引けるようにする。
    const index: Record<string, number> = { east: 0, west: 1, up: 2, down: 3, south: 4, north: 5 };
    if (index[facing] !== undefined) faces[index[facing]] = front;
  }
  return faces;
}

function pillarFaces(base: string, axis: string): BlockAppearance['faces'] {
  const side = base;
  const end = `${base}_top`;
  if (axis === 'x') return [end, end, side, side, side, side];
  if (axis === 'z') return [side, side, side, side, end, end];
  return [side, side, end, end, side, side];
}

function stairBoxes(properties: Record<string, string>): BlockBox[] {
  const facing = properties.facing ?? 'north';
  const top = properties.half === 'top';
  const slab: BlockBox = top ? { from: [0, 0.5, 0], to: [1, 1, 1] } : { from: [0, 0, 0], to: [1, 0.5, 1] };
  const highHalf = (direction: string, x: number, z: number) => {
    switch (direction) {
      case 'south': return z === 1;
      case 'east': return x === 1;
      case 'west': return x === 0;
      default: return z === 0;
    }
  };
  const left: Record<string, string> = { north: 'west', south: 'east', east: 'north', west: 'south' };
  const right: Record<string, string> = { north: 'east', south: 'west', east: 'south', west: 'north' };
  const shape = properties.shape ?? 'straight';
  const side = shape.endsWith('_left') ? left[facing] : right[facing];
  if (shape === 'straight') {
    const extent: Record<string, [number, number, number, number]> = {
      north: [0, 0, 1, 0.5], south: [0, 0.5, 1, 1],
      east: [0.5, 0, 1, 1], west: [0, 0, 0.5, 1],
    };
    const [x0, z0, x1, z1] = extent[facing] ?? extent.north;
    return [slab, { from: [x0, top ? 0 : 0.5, z0], to: [x1, top ? 0.5 : 1, z1] }];
  }
  const boxes: BlockBox[] = [slab];
  for (let x = 0; x < 2; x++) for (let z = 0; z < 2; z++) {
    const main = highHalf(facing, x, z);
    const turn = highHalf(side, x, z);
    const filled = shape.startsWith('inner_') ? main || turn : shape.startsWith('outer_') ? main && turn : main;
    if (filled) boxes.push({ from: [x / 2, top ? 0 : 0.5, z / 2], to: [(x + 1) / 2, top ? 0.5 : 1, (z + 1) / 2] });
  }
  return boxes;
}

function connectedBoxes(properties: Record<string, string>, half: number, height: number, post: number): BlockBox[] {
  const low = 0.5 - half;
  const high = 0.5 + half;
  const boxes: BlockBox[] = [{ from: [0.5 - post, 0, 0.5 - post], to: [0.5 + post, height, 0.5 + post] }];
  const links: Array<[string, BlockBox]> = [
    ['north', { from: [low, 0, 0], to: [high, height, 0.5] }],
    ['south', { from: [low, 0, 0.5], to: [high, height, 1] }],
    ['west', { from: [0, 0, low], to: [0.5, height, high] }],
    ['east', { from: [0.5, 0, low], to: [1, height, high] }],
  ];
  const connected = links.filter(([side]) => properties[side] === 'true' || properties[side] === 'low' || properties[side] === 'tall');
  if (connected.length === 0 && Object.keys(properties).length === 0) {
    return [
      { from: [low, 0, 0], to: [high, height, 1] },
      { from: [0, 0, low], to: [1, height, high] },
    ];
  }
  connected.forEach(([, box]) => boxes.push(box));
  return boxes;
}

function wallBox(facing: string, depth: number): BlockBox {
  switch (facing) {
    case 'south': return { from: [0, 0, 1 - depth], to: [1, 1, 1] };
    case 'east': return { from: [1 - depth, 0, 0], to: [1, 1, 1] };
    case 'west': return { from: [0, 0, 0], to: [depth, 1, 1] };
    default: return { from: [0, 0, 0], to: [1, 1, depth] };
  }
}

/** 接続の有無。Java版は`side`/`up`、こちらで補ったものは`side`、繋がっていなければ`none`。 */
function linked(value: string | undefined): boolean {
  return value === 'side' || value === 'up' || value === 'true';
}

/** 向いている方向を「手前0〜奥1」として、その軸に沿った箱を作る。ピストンの腕に使う。 */
function alongFacing(facing: string, from: number, to: number, half: number): BlockBox {
  const low = 0.5 - half;
  const high = 0.5 + half;
  switch (facing) {
    case 'up': return { from: [low, from, low], to: [high, to, high] };
    case 'down': return { from: [low, 1 - to, low], to: [high, 1 - from, high] };
    case 'south': return { from: [low, low, from], to: [high, high, to] };
    case 'north': return { from: [low, low, 1 - to], to: [high, high, 1 - from] };
    case 'east': return { from: [from, low, low], to: [to, high, high] };
    default: return { from: [1 - to, low, low], to: [1 - from, high, high] };
  }
}

/** トリップワイヤーフックは、facingの反対側の壁に取り付く小さな金具。 */
function hookBox(facing: string): BlockBox {
  const depth = 0.1875;
  const low = 0.375;
  const high = 0.625;
  switch (facing) {
    case 'south': return { from: [low, 0.25, 0], to: [high, 0.6875, depth] };
    case 'north': return { from: [low, 0.25, 1 - depth], to: [high, 0.6875, 1] };
    case 'east': return { from: [0, 0.25, low], to: [depth, 0.6875, high] };
    default: return { from: [1 - depth, 0.25, low], to: [1, 0.6875, high] };
  }
}

/** レッドストーン装置の形。立方体のままだと仕掛けの区別がつかないので個別に持つ。 */
function redstoneShape(name: string, properties: Record<string, string>): { boxes: BlockBox[]; full: boolean } | undefined {
  if (name === 'redstone_wire') {
    // 中央の点から、繋がっている方向へだけ線を伸ばす。Minecraftと同じ見え方になる。
    const height = 0.0625;
    const boxes: BlockBox[] = [{ from: [0.3125, 0, 0.3125], to: [0.6875, height, 0.6875] }];
    if (linked(properties.north)) boxes.push({ from: [0.375, 0, 0], to: [0.625, height, 0.3125] });
    if (linked(properties.south)) boxes.push({ from: [0.375, 0, 0.6875], to: [0.625, height, 1] });
    if (linked(properties.west)) boxes.push({ from: [0, 0, 0.375], to: [0.3125, height, 0.625] });
    if (linked(properties.east)) boxes.push({ from: [0.6875, 0, 0.375], to: [1, height, 0.625] });
    return { boxes, full: false };
  }
  if (name === 'rail' || /_rail$/.test(name)) {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.0625, 1] }], full: false };
  }
  if (name === 'tripwire') {
    // フックとフックを結ぶ1本の線。両方向に繋がっているときだけ十字になる。
    const alongX = linked(properties.east) || linked(properties.west);
    const alongZ = linked(properties.north) || linked(properties.south);
    const boxes: BlockBox[] = [];
    if (alongX) boxes.push({ from: [0, 0.0625, 0.4375], to: [1, 0.125, 0.5625] });
    if (alongZ) boxes.push({ from: [0.4375, 0.0625, 0], to: [0.5625, 0.125, 1] });
    if (boxes.length === 0) boxes.push({ from: [0.4375, 0.0625, 0.4375], to: [0.5625, 0.125, 0.5625] });
    return { boxes, full: false };
  }
  if (name === 'tripwire_hook') {
    return { boxes: [hookBox(properties.facing ?? 'north')], full: false };
  }
  if (name === 'piston_head' || name === 'sticky_piston_head') {
    const facing = properties.facing ?? 'up';
    return { boxes: [alongFacing(facing, 0.75, 1, 0.5), alongFacing(facing, 0, 0.75, 0.125)], full: false };
  }
  if (name === 'lever') {
    return {
      boxes: [
        { from: [0.3125, 0, 0.3125], to: [0.6875, 0.1875, 0.6875] },
        { from: [0.4375, 0.1875, 0.4375], to: [0.5625, 0.625, 0.5625] },
      ],
      full: false,
    };
  }
  if (name === 'repeater' || name === 'comparator') {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.125, 1] }], full: false };
  }
  if (name === 'daylight_detector') {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.375, 1] }], full: false };
  }
  if (name === 'hopper') {
    return {
      boxes: [
        { from: [0, 0.625, 0], to: [1, 1, 1] },
        { from: [0.25, 0.25, 0.25], to: [0.75, 0.625, 0.75] },
      ],
      full: false,
    };
  }
  return undefined;
}

function shapeFor(name: string, suffix: string, properties: Record<string, string>): { boxes: BlockBox[]; full: boolean } {
  const redstone = suffix ? undefined : redstoneShape(name, properties);
  if (redstone) return redstone;

  switch (suffix) {
    case '_slab': {
      if (properties.type === 'double') return { boxes: [FULL_CUBE], full: true };
      const box: BlockBox = properties.type === 'top'
        ? { from: [0, 0.5, 0], to: [1, 1, 1] }
        : { from: [0, 0, 0], to: [1, 0.5, 1] };
      return { boxes: [box], full: false };
    }
    case '_stairs':
      return { boxes: stairBoxes(properties), full: false };
    case '_wall':
      return { boxes: connectedBoxes(properties, 0.125, 1, 0.25), full: false };
    case '_fence': {
      const [post, ...links] = connectedBoxes({ north: 'false', ...properties }, 0.0625, 1, 0.125);
      return { boxes: [post, ...links.flatMap((link) => [
        { from: [link.from[0], 0.375, link.from[2]], to: [link.to[0], 0.5625, link.to[2]] } as BlockBox,
        { from: [link.from[0], 0.75, link.from[2]], to: [link.to[0], 0.9375, link.to[2]] } as BlockBox,
      ])], full: false };
    }
    case '_pane':
    case '_bars':
      return { boxes: connectedBoxes(properties, 0.0625, 1, 0.0625), full: false };
    case '_fence_gate': {
      const facing = properties.facing ?? 'north';
      const alongX = facing === 'north' || facing === 'south';
      const box: BlockBox = alongX
        ? { from: [0, 0.3125, 0.4375], to: [1, 1, 0.5625] }
        : { from: [0.4375, 0.3125, 0], to: [0.5625, 1, 1] };
      return { boxes: [box], full: false };
    }
    case '_door':
      return { boxes: [wallBox(properties.facing ?? 'north', 0.1875)], full: false };
    case '_trapdoor': {
      if (properties.open === 'true') return { boxes: [wallBox(properties.facing ?? 'north', 0.1875)], full: false };
      const box: BlockBox = properties.half === 'top'
        ? { from: [0, 0.8125, 0], to: [1, 1, 1] }
        : { from: [0, 0, 0], to: [1, 0.1875, 1] };
      return { boxes: [box], full: false };
    }
    case '_pressure_plate':
      return { boxes: [{ from: [0.0625, 0, 0.0625], to: [0.9375, 0.0625, 0.9375] }], full: false };
    case '_button':
      return { boxes: [{ from: [0.3125, 0, 0.375], to: [0.6875, 0.125, 0.625] }], full: false };
    case '_carpet':
      return { boxes: [{ from: [0, 0, 0], to: [1, 0.0625, 1] }], full: false };
    case '_sign':
      return { boxes: [{ from: [0, 0.25, 0.4375], to: [1, 1, 0.5625] }], full: false };
    default:
      break;
  }

  if (name === 'snow') {
    const layers = Number(properties.layers ?? '1');
    const height = Math.min(8, Math.max(1, Number.isFinite(layers) ? layers : 1)) / 8;
    return { boxes: [{ from: [0, 0, 0], to: [1, height, 1] }], full: height >= 1 };
  }
  if (name === 'ladder' || name === 'painting') {
    return { boxes: [wallBox(properties.facing ?? 'north', 0.0625)], full: false };
  }
  if (/torch$/.test(name)) {
    return { boxes: [{ from: [0.4375, 0, 0.4375], to: [0.5625, 0.625, 0.5625] }], full: false };
  }
  if (name === 'campfire' || name === 'soul_campfire') {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.4375, 1] }], full: false };
  }
  if (name === 'lantern' || name === 'soul_lantern') {
    const hanging = properties.hanging === 'true';
    const box: BlockBox = hanging
      ? { from: [0.3125, 0.4375, 0.3125], to: [0.6875, 1, 0.6875] }
      : { from: [0.3125, 0, 0.3125], to: [0.6875, 0.5625, 0.6875] };
    return { boxes: [box], full: false };
  }
  if (name === 'end_rod' || name === 'lightning_rod' || name === 'chain') {
    return { boxes: [{ from: [0.4375, 0, 0.4375], to: [0.5625, 1, 0.5625] }], full: false };
  }
  if (name === 'flower_pot' || /^potted_/.test(name)) {
    return { boxes: [{ from: [0.3125, 0, 0.3125], to: [0.6875, 0.375, 0.6875] }], full: false };
  }
  if (name === 'cake') {
    return { boxes: [{ from: [0.0625, 0, 0.0625], to: [0.9375, 0.5, 0.9375] }], full: false };
  }
  if (name === 'enchanting_table') {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.75, 1] }], full: false };
  }
  if (name === 'chest' || name === 'trapped_chest' || name === 'ender_chest') {
    return { boxes: [{ from: [0.0625, 0, 0.0625], to: [0.9375, 0.875, 0.9375] }], full: false };
  }
  if (/(_bed|^bed$)/.test(name)) {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.5625, 1] }], full: false };
  }
  if (name === 'lily_pad') {
    return { boxes: [{ from: [0, 0, 0], to: [1, 0.0625, 1] }], full: false };
  }

  return { boxes: [FULL_CUBE], full: true };
}

function textureFor(name: string, suffix: string, base: string, properties: Record<string, string>): BlockAppearance['faces'] {
  // 信号が通っているかで色が変わる。教材では「トーチが消える」ことそのものが要点になる。
  if (name === 'redstone_wire') {
    const power = Number(properties.power ?? '0');
    const tone = Number.isFinite(power) && power > 0 ? 'redstone_dust_on' : 'redstone_dust';
    return [tone, tone, tone, tone, tone, tone];
  }
  if (name === 'redstone_torch' || name === 'redstone_wall_torch') {
    const tone = properties.lit === 'false' ? 'redstone_torch_off' : 'redstone_torch';
    return [tone, tone, tone, tone, tone, tone];
  }
  // たき火は丸太の台に炎の天面。消えているときは丸太だけにする。
  if (name === 'campfire' || name === 'soul_campfire') {
    const out = properties.extinguished === 'true' || properties.lit === 'false';
    const top = out ? 'campfire_log' : name === 'campfire' ? 'campfire_fire' : 'soul_campfire_fire';
    return ['campfire_log', 'campfire_log', top, 'campfire_log', 'campfire_log', 'campfire_log'];
  }

  if (suffix === '_door' || suffix === '_trapdoor') {
    const texture = suffix === '_door' ? `${name}_${properties.half === 'upper' ? 'top' : 'bottom'}` : name;
    return [texture, texture, texture, texture, texture, texture];
  }
  if (suffix === '_carpet' || suffix === '_pane') {
    const texture = suffix === '_carpet' ? `${base}_wool` : base;
    return [texture, texture, texture, texture, texture, texture];
  }
  if (name === 'redstone_lamp' && properties.lit === 'true') {
    return Array(6).fill('redstone_lamp_on') as unknown as BlockAppearance['faces'];
  }
  const explicit = explicitFaces[name];
  if (explicit) {
    const side = explicit.side ?? name;
    return facesFrom(explicit.top ?? side, side, explicit.bottom ?? explicit.top ?? side, explicit.front, properties.facing ?? 'north');
  }

  if (/(_log|_stem|_hyphae|_wood|_bamboo_block)$/.test(name) || /_pillar$/.test(name) || name === 'basalt' || name === 'polished_basalt') {
    const stripped = /(_wood|_hyphae)$/.test(name) ? name.replace(/(_wood|_hyphae)$/, (match) => (match === '_wood' ? '_log' : '_stem')) : name;
    if (/(_wood|_hyphae)$/.test(name)) {
      return [stripped, stripped, stripped, stripped, stripped, stripped];
    }
    return pillarFaces(stripped, properties.axis ?? 'y');
  }

  const material = textureBaseColors[name] !== undefined
    ? name
    : suffix ? materialTexture(base) : materialTexture(name);
  const explicitMaterial = explicitFaces[material];
  if (suffix && explicitMaterial) {
    const side = explicitMaterial.side ?? material;
    return facesFrom(explicitMaterial.top ?? side, side, explicitMaterial.bottom ?? explicitMaterial.top ?? side);
  }
  return [material, material, material, material, material, material];
}

/**
 * ブロック名とプロパティから、面ごとのテクスチャ・形状・描画レイヤーを決める。
 * 未知のブロックはフルキューブとして扱い、名前から推定したテクスチャを使う。
 */
export function blockAppearance(rawName: string, properties: Record<string, string> = {}): BlockAppearance {
  const name = stripNamespace(rawName);
  const { base, suffix } = stripSuffix(name);
  const cross = CROSS.test(name) && !/(block|_slab|_stairs|_wall|_fence)/.test(name);
  const shape = cross ? { boxes: [FULL_CUBE], full: false } : shapeFor(name, suffix, properties);
  const layer: RenderLayer = TRANSLUCENT.test(name) ? 'translucent' : 'solid';
  const transparent = layer === 'translucent' || cross || /leaves/.test(name);

  return {
    faces: textureFor(name, suffix, base, properties),
    boxes: shape.boxes,
    layer,
    occludes: shape.full && !transparent,
    mergesWithSame: transparent && !cross,
    // ランプと鉱石は点灯しているときだけ光る。消えたトーチも光らせない。
    emissive: EMISSIVE.test(name)
      && (/(redstone_lamp|redstone_ore)/.test(name) ? properties.lit === 'true' : properties.lit !== 'false')
      && properties.extinguished !== 'true',
    cross,
  };
}
